<template>
  <div class="relative w-fit">
    <pre
      class="rounded-lg text-sm"
      :class="[
        props.wrap ? 'whitespace-pre-wrap' : 'overflow-x-auto whitespace-pre',
        props.minimal ? 'pr-10' : 'border border-secondary p-3 pr-10',
      ]"
    ><code v-html="highlightedCode"></code></pre>

    <div
      v-if="!props.wrap && !props.minimal"
      class="pointer-events-none absolute right-0 top-0 h-full w-16 rounded-r-lg"
    ></div>

    <div v-if="!props.minimal" class="absolute right-2 z-10" :class="props.minimal ? 'top-1.5' : 'top-2'">
      <CopyButton
        :value="codeString"
        wrapper-class="inline-flex"
        :button-class="[
          'text-gray-400 hover:text-gray-200 rounded transition-colors opacity-70 hover:opacity-100',
          props.minimal ? 'p-1' : 'p-1.5 hover:bg-gray-800',
        ]"
        copied-text-class="text-green-400 text-sm font-medium"
        copied-text-position="above"
      >
        <DocumentDuplicateIcon class="h-4 w-4 text-current" />
      </CopyButton>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import CopyButton from "./CopyButton.vue";
import { DocumentDuplicateIcon } from "@heroicons/vue/24/outline";

const props = defineProps({
  code: {
    type: [String, Array],
    required: true,
  },
  wrap: {
    type: Boolean,
    default: true,
  },
  highlight: {
    type: Boolean,
    default: true,
  },
  minimal: {
    type: Boolean,
    default: false,
  },
});

const codeLines = computed(() => {
  if (Array.isArray(props.code)) {
    return props.code;
  }
  return props.code.split("\n");
});

const codeString = computed(() => {
  return codeLines.value.join("\n");
});

const showLineNumbers = computed(() => {
  return codeLines.value.length > 1;
});

const highlightedCode = computed(() => {
  const lines = codeLines.value;
  const shouldHighlight = props.highlight;

  if (showLineNumbers.value) {
    // Calculate the width needed for line numbers
    const maxLineNumber = lines.length;
    const lineNumberWidth = maxLineNumber.toString().length;

    return lines
      .map((line, index) => {
        const lineNumber = (index + 1).toString().padStart(lineNumberWidth, " ");
        const highlightedLine = shouldHighlight ? highlightBash(line) : escapeHtml(line);
        return `<span class="text-gray-500 select-none mr-4">${lineNumber}</span>${highlightedLine}`;
      })
      .join("\n");
  } else {
    const singleLine = lines[0] || "";
    return shouldHighlight ? highlightBash(singleLine) : escapeHtml(singleLine);
  }
});

const escapeHtml = (text) => {
  const map = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };
  return text.replace(/[&<>"']/g, (m) => map[m]);
};

const highlightBash = (code) => {
  const patterns = [
    { name: "comment", regex: /#.*$/gm, class: "text-gray-500" },
    { name: "string", regex: /(["'])(?:(?=(\\?))\2.)*?\1/g, class: "text-green-300" },
    { name: "variable", regex: /\$\w+|\$\{[^}]*\}/g, class: "text-yellow-300" },
    { name: "flag", regex: /(?:\s|^)(-[a-zA-Z]+)/g, class: "text-purple-300" },
    {
      name: "number",
      regex: /(?<![\/:\w-])\b\d+\b(?![\/:\w-])/g,
      class: "text-orange-300",
    },
    { name: "path", regex: /(?:\s|^)(\/[^\s]*)/g, class: "text-cyan-300" },
    {
      name: "keyword",
      regex: new RegExp(
        `\\b(${[
          "curl",
          "echo",
          "ls",
          "cd",
          "mkdir",
          "rm",
          "cp",
          "mv",
          "chmod",
          "chown",
          "grep",
          "find",
          "sed",
          "awk",
          "cat",
          "less",
          "more",
          "tail",
          "head",
          "sort",
          "uniq",
          "wc",
          "ps",
          "kill",
          "jobs",
          "bg",
          "fg",
          "nohup",
          "sudo",
          "su",
          "export",
          "source",
          "alias",
          "if",
          "then",
          "else",
          "elif",
          "fi",
          "for",
          "while",
          "do",
          "done",
          "case",
          "esac",
          "function",
          "return",
          "exit",
          "break",
          "continue",
          "bash",
          "socat",
          "stdio",
        ].join("|")})\\b`,
        "g",
      ),
      class: "text-blue-300",
    },
  ];

  const replacements = [];

  // Collect matches
  patterns.forEach(({ regex, class: className }) => {
    let match;
    while ((match = regex.exec(code)) !== null) {
      replacements.push({
        start: match.index,
        end: match.index + match[0].length,
        content: `<span class="${className}">${escapeHtml(match[0])}</span>`,
      });
    }
  });

  // Sort and remove overlaps
  replacements.sort((a, b) => a.start - b.start);
  const nonOverlapping = [];
  let lastIndex = 0;
  for (const r of replacements) {
    if (r.start >= lastIndex) {
      nonOverlapping.push(r);
      lastIndex = r.end;
    }
  }

  // Rebuild string with highlights
  let highlighted = "";
  let currentIndex = 0;
  for (const r of nonOverlapping) {
    highlighted += escapeHtml(code.slice(currentIndex, r.start)) + r.content;
    currentIndex = r.end;
  }
  highlighted += escapeHtml(code.slice(currentIndex));

  return highlighted;
};
</script>
